import { useMemo } from "react";
import BigNumber from "bignumber.js";
import type { Environment } from "../../../types";
import { CHAIN_IDS } from "../constants";
import { useRewardPools } from "./use-reward-pools";
import { useTokenBalance } from "../../wallet-info/queries/use-token-balance";
import {
  useTokenStats,
  buildStatsKey,
} from "../../wallet-info/queries/use-token-stats";

function isCrossSymbol(symbol: string | undefined): boolean {
  return symbol?.trim().toUpperCase() === "ONE";
}

function isPositivePrice(price: string | undefined): boolean {
  return new BigNumber(price || "0").isGreaterThan(0);
}

/**
 * CROSS/USD 단가(문자열)를 해석한다. 못 구하면 "0".
 *
 * Stake/Forge 섹션은 CROSS 단위 금액을 USD로 환산해야 하는데, token-stats 응답에는
 * symbol이 없어 CROSS 항목을 주소 없이 직접 찾을 수 없다. 그래서 두 소스를 둔다:
 *
 *  1순위) reward 풀 토큰(deposit/reward)에 USD price와 함께 내려오는 CROSS.
 *         pool config라서 사용자 보유와 무관하게 존재 → CROSS 잔고가 0인(스테이킹만
 *         한) 유저도 커버된다. 이미 fetch 중인 쿼리라 추가 비용이 없다.
 *  2순위) 1순위 실패 시에만, 잔고의 cross 체인 "CROSS" 토큰 주소로 token-stats
 *         단가를 조회한다. (이 fallback일 때만 잔고/스탯 쿼리를 활성화한다.)
 */
export function useCrossUsdPrice(
  env: Environment,
  walletAddress: string,
  enabled = true,
): string {
  const { allPools } = useRewardPools(env, enabled);

  const rewardCrossPrice = useMemo(() => {
    for (const pool of allPools) {
      for (const token of [pool.deposit_token, ...pool.reward_tokens]) {
        if (isCrossSymbol(token?.symbol) && isPositivePrice(token.price)) {
          return token.price;
        }
      }
    }
    return undefined;
  }, [allPools]);

  // 1순위가 없을 때만 잔고/스탯 쿼리를 켜서 불필요한 폴링을 피한다.
  const needFallback = enabled && rewardCrossPrice === undefined;
  const { tokens } = useTokenBalance(env, walletAddress, needFallback);
  const { statsMap } = useTokenStats(env, needFallback);
  const crossChainId = CHAIN_IDS[env].cross;

  return useMemo(() => {
    if (rewardCrossPrice) return rewardCrossPrice;
    const crossToken = tokens.find(
      (t) => t.chainId === crossChainId && isCrossSymbol(t.symbol),
    );
    if (crossToken) {
      const stat = statsMap.get(
        buildStatsKey(crossToken.chainId, crossToken.address),
      );
      if (stat && isPositivePrice(stat.price)) return stat.price;
    }
    return "0";
  }, [rewardCrossPrice, tokens, statsMap, crossChainId]);
}
